import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';

import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import {
    ContactDetailsSchema,
    type ContactDetailsSchemaType,
} from '@/features/administration/contract/validations/contact';
import { useCountryCodesPhone } from '@/hooks/useCountryCodesPhone';
import { useLanguageNames } from '@/hooks/useLanguageNames';
import { buildPhoneNumber, t } from '@/lib/utils';

export type ContactDetailsProps = {
    contactData?: ContactDetailsSchemaType;
    saveChanges: (contactData: ContactDetailsSchemaType) => Promise<void>;
    handleCancel: () => void;
    loading: boolean;
    hasWritePermissions?: boolean;
};

const ContactDetailsComponent = forwardRef<UseFormReturn<ContactDetailsSchemaType>, ContactDetailsProps>(
    ({ contactData, saveChanges, handleCancel, loading, hasWritePermissions }, ref) => {
        const countryCodes = useCountryCodesPhone;
        const languageNames = useLanguageNames;

        const form = useForm<ContactDetailsSchemaType>({
            resolver: zodResolver(ContactDetailsSchema()),
            defaultValues: contactData,
        });

        const {
            control,
            register,
            handleSubmit,
            formState: { errors, isDirty },
            reset,
        } = form;

        useImperativeHandle(ref, () => form);

        useEffect(() => {
            if (contactData) {
                reset(contactData);
            }
        }, [contactData, reset]);

        const onSubmit = (data: ContactDetailsSchemaType) => {
            saveChanges({
                ...data,
                rawPhone: buildPhoneNumber(data.countryCode, data.phone),
            });
        };

        return (
            <div className='display-flex flex-1-1-0 flex-column margin-bottom-15 bg-white rounded'>
                <div className='display-flex justify-content-between'>
                    <div className='text-size-18 text-bold margin-bottom-15'>
                        {t('chargingContract.contactDetails.title')}
                    </div>
                </div>
                <div className='margin-bottom-15'>
                    <span>
                        {t(
                            hasWritePermissions
                                ? 'chargingContract.contactDetails.description'
                                : 'chargingContract.contactDetails.description.fleetUser'
                        )}
                    </span>
                </div>
                <div className='display-flex gap-15'>
                    <Input
                        label={t('general.personalEmailAddress')}
                        className='flex-1-1-0'
                        required
                        error={errors?.email?.message}
                        inputAttributes={{ ...register('email'), disabled: loading || !hasWritePermissions }}
                    />
                    <Controller
                        name='communicationLanguage'
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <SelectInput
                                required
                                className='flex-1-1-0'
                                error={errors.communicationLanguage?.message}
                                label={t('general.languageOfCommunication')}
                                selectAttributes={{
                                    onChange: item => onChange(item?.id),
                                    options: languageNames(value),
                                    disabled: loading || !hasWritePermissions,
                                }}
                            />
                        )}
                    />
                </div>
                <div className='display-flex margin-bottom-25 max-width-50pct'>
                    <span className='uikit-icon icon-info-sign padding-top-3' />
                    <i className='margin-left-5'>{t('general.sendAllImportantCommunication')}</i>
                </div>
                <div className='margin-bottom-5'>
                    <Controller
                        name='countryCode'
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className='display-flex gap-10'>
                                <SelectInput
                                    required
                                    className='min-width-auto width-auto'
                                    error={errors.countryCode?.message}
                                    label={t('general.countryCode')}
                                    selectAttributes={{
                                        onChange: item => onChange(item?.id),
                                        options: countryCodes(value),
                                        disabled: loading || !hasWritePermissions,
                                    }}
                                />
                                <Input
                                    required
                                    className='flex-1-1-0'
                                    label={t('general.contactPhoneNumber')}
                                    error={errors?.phone?.message}
                                    inputAttributes={{
                                        ...register('phone'),
                                        disabled: loading || !hasWritePermissions,
                                    }}
                                />
                            </div>
                        )}
                    />
                </div>
                {hasWritePermissions && (
                    <div className='margin-top-25 display-flex justify-content-end gap-10'>
                        <Button disabled={!isDirty || loading} className='width-120' onClick={handleCancel}>
                            <span>{t('general.button.cancel')}</span>
                        </Button>
                        <Button
                            className={`width-120 ${loading ? 'btn-loading' : ''}`}
                            disabled={!isDirty || loading}
                            bsStyle='primary'
                            onClick={handleSubmit(onSubmit)}
                        >
                            <span>{t('general.button.saveChanges')}</span>
                        </Button>
                    </div>
                )}
            </div>
        );
    }
);

ContactDetailsComponent.displayName = 'ContactDetails';

export const ContactDetails = ContactDetailsComponent;
